import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout, PasswordField } from "@/components/AuthLayout";
import { saveProfile } from "@/data/practiceData";
import { apiFetch, setToken } from "@/api";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    setError("");
    setSaving(true);
    try {
      const data = await apiFetch("/auth/reset-password", {
        method: "POST",
        body: { token, password },
      });
      setToken(data.token);
      saveProfile({ name: data.user.name });
      navigate(data.user?.board ? "/dashboard" : "/practice-setup");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AuthLayout
      title="Choose a new password"
      subtitle="Pick something you'll remember."
      footer={
        <>
          Back to{" "}
          <Link
            to="/login"
            data-testid="reset-back-login-link"
            className="font-bold text-[#16A34A] hover:underline"
          >
            login
          </Link>
        </>
      }
    >
      {!token ? (
        <div data-testid="reset-no-token" className="text-center">
          <p className="text-sm font-semibold leading-relaxed text-[#17201A]/80">
            This reset link is missing its token. Please request a fresh one.
          </p>
          <Link
            to="/forgot-password"
            data-testid="reset-request-new-link"
            className="mt-4 inline-block text-sm font-bold text-[#16A34A] hover:underline"
          >
            Request a new reset link
          </Link>
        </div>
      ) : (
        <form
          data-testid="reset-password-form"
          onSubmit={handleSubmit}
          className="space-y-4"
          noValidate
        >
          <PasswordField
            label="New Password"
            testid="reset-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 6 characters"
            error={error}
          />
          <button
            type="submit"
            data-testid="reset-submit-btn"
            data-magnetic="true"
            disabled={saving}
            className="w-full rounded-full bg-[#16A34A] py-4 text-base font-bold text-white shadow-sm transition-[background-color,transform] duration-200 hover:bg-[#15803D] disabled:opacity-60"
          >
            {saving ? "Saving..." : "Set New Password"}
          </button>
          {error && /expired|invalid|already been used/i.test(error) && (
            <div className="text-center">
              <Link
                to="/forgot-password"
                data-testid="reset-request-new-link"
                className="text-xs font-bold text-[#16A34A] hover:underline"
              >
                Request a new reset link
              </Link>
            </div>
          )}
        </form>
      )}
    </AuthLayout>
  );
};

export default ResetPassword;
